import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PermissionsContext = createContext(null);

export const PermissionsProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [perms, setPerms] = useState(null); // {is_admin, app}
  const [studyCache, setStudyCache] = useState({}); // {studyId: sectionMap}

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/permissions/me`, { withCredentials: true });
      setPerms(r.data);
      setStudyCache({});
    } catch (_e) {
      setPerms(null);
    }
  }, []);

  // Refresh permissions only when there is an authenticated user. This avoids
  // a pre-login 401 on /api/permissions/me racing with the login flow.
  useEffect(() => {
    if (authLoading) return;
    if (user && user !== false) refresh();
    else setPerms(null);
  }, [user, authLoading, refresh]);

  const can = useCallback((resource, action = 'view') => {
    if (!perms) return false;
    if (perms.is_admin) return true;
    return !!(perms.app?.[resource]?.[action]);
  }, [perms]);

  const loadStudyPerms = useCallback(async (studyId) => {
    if (!studyId) return null;
    if (studyCache[studyId]) return studyCache[studyId];
    try {
      const r = await axios.get(`${API_URL}/api/permissions/me/study/${studyId}`, { withCredentials: true });
      setStudyCache(prev => ({ ...prev, [studyId]: r.data }));
      return r.data;
    } catch (_e) {
      return null;
    }
  }, [studyCache]);

  const canStudy = useCallback((studyId, section, action = 'view') => {
    if (!perms) return false;
    if (perms.is_admin) return true;
    const s = studyCache[studyId];
    if (!s) return false;
    return !!(s?.[section]?.[action]);
  }, [perms, studyCache]);

  return (
    <PermissionsContext.Provider value={{ perms, can, canStudy, loadStudyPerms, refresh, isAdmin: perms?.is_admin || false }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) return { perms: null, can: () => false, canStudy: () => false, loadStudyPerms: async () => null, refresh: () => {}, isAdmin: false };
  return ctx;
};
